import type { OnClickEvent } from '@/core/types/react-overrides';

import React from 'react';

import { WindowCloseIcon } from '@/assets/icons/window-close';
import KeyboardNavigationContainerDesktop from '@/core/components/dropdown/keyboard-navigation-container-desktop';
import KeyboardNavigationItemDesktop from '@/core/components/dropdown/keyboard-navigation-item-desktop';
import { Button } from '@/core/features';
import { useMetaDataState } from '@/core/features/a-dynamic-page/hooks/meta-data-state';
import useOutsideClick from '@/core/features/event-listener/use-outside-click';
import color from '@/core/features/styles/color';
import zIndex from '@/core/features/styles/generated/z-index';
import { useCollapseTravelForm } from '@/features/travel-form/travel-form-desktop/travel-form-desktop';

type TravelFormInputDesktopProps = React.PropsWithChildren<{
    autoFocus?: boolean;
    childrenContainerMaxHeight?: number;
    ctaText?: string;
    errorLabel?: string;
    filterValue: null | string;
    hasError?: boolean;
    hideCta?: boolean;
    inputSize: 'large' | 'normal';
    isCtaDisabled?: boolean;
    isInputDisabled?: boolean;
    isLastOverlay?: boolean;
    isLoading?: boolean;
    isOverlayVisible: boolean;
    label: string;
    maxHeight?: number;
    minWidth?: '100%' | number;
    onChange?: (value: string) => void;
    onClear?: () => void;
    onClick: (event: OnClickEvent) => void;
    onClose?: () => void;
    onCtaClick?: () => void;
    onOutsideClick?: () => void;
    openNextInputOverlay?: () => void;
    placeholder: string;
    qaId?: string;
    type: 'cta' | 'dropdown' | 'input';
}>;

export default function TravelFormInputDesktop({
    autoFocus = false,
    children,
    childrenContainerMaxHeight = 344,
    ctaText,
    errorLabel,
    filterValue,
    hasError,
    hideCta,
    inputSize,
    isCtaDisabled,
    isInputDisabled = false,
    isLastOverlay = false,
    isLoading,
    isOverlayVisible,
    label,
    maxHeight = 414,
    minWidth = 414,
    onChange,
    onClear,
    onClick,
    onClose,
    onCtaClick,
    onOutsideClick,
    openNextInputOverlay,
    placeholder,
    qaId,
    type,
}: TravelFormInputDesktopProps) {
    const [inputValue, setInputValue] = React.useState<string>('');
    const inputWrapperRef = React.useRef<HTMLDivElement | null>(null);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const containerRef = useOutsideClick(() => {
        /* The onClose callback of the first parent component that uses this component is always used here first.
		To ensure that the onClose callback is only executed for the currently open overlay, this guard is added. */
        if (!isOverlayVisible) {
            return;
        }
        clearInput();
        onOutsideClick?.();
        onCtaClick?.();
        onClose?.();
    });

    const isTabbingForward = React.useRef(false);
    const [isCtaHighlighted, setIsCtaHighlighted] = React.useState(false);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        isTabbingForward.current = event.key === 'Tab' && !event.shiftKey;
    };

    const isClearable = inputRef.current?.value && inputValue !== '' && type === 'input' && !isInputDisabled;

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        onChange?.(event.target.value);
    };

    const clearInput = () => {
        if (!inputRef?.current) {
            return;
        }
        inputRef.current.value = '';
        setInputValue('');
    };

    const handleOnClear = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        clearInput();
        onClear?.();
    };

    React.useEffect(() => {
        if (inputRef.current && type === 'input') {
            inputRef.current.disabled = isInputDisabled;
            if (!isInputDisabled || isOverlayVisible) {
                return;
            }
        }
    }, [isOverlayVisible, containerRef, type, isInputDisabled]);

    const handleEscClick = React.useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                clearInput();
                onCtaClick?.();
                onClose?.();
            }
        },
        [onClose, onCtaClick],
    );

    React.useEffect(() => {
        if (!isOverlayVisible) {
            document.removeEventListener('keydown', handleEscClick);
            return;
        }

        document.addEventListener('keydown', handleEscClick);

        return () => {
            document.removeEventListener('keydown', handleEscClick);
        };
    }, [handleEscClick, isOverlayVisible]);

    const { expandTravelForm } = useCollapseTravelForm();
    const handleOnClick = (event: OnClickEvent) => {
        event.stopPropagation();
        expandTravelForm({ delay: 500 });

        if (type === 'cta' || type === 'dropdown') {
            if (type === 'dropdown' && isOverlayVisible) {
                clearInput();
                onCtaClick?.();
            }
            onClick(event);
            return;
        }
        if (!inputRef?.current) {
            return;
        }
        onClick(event);
    };

    const handleOnCtaClick = () => {
        clearInput();
        onCtaClick?.();
    };

    const inputOverlayMinWidth = (() => {
        if (type !== 'dropdown' || minWidth) {
            return minWidth === '100%' ? minWidth : `${minWidth}px`;
        }

        if (inputWrapperRef.current) {
            return `${inputWrapperRef.current.offsetWidth}px`;
        }

        return undefined;
    })();

    const meta = useMetaDataState();
    const isAsideRightLayout = meta?.layoutId === 'Desktop_Aside_Right_V1';

    return (
        <div
            className={'flex relative flex-col gap-1 w-full'}
            data-qa-id={qaId}
            /* Workaround for handling outside clicks, as every click on the overlay content triggers the closure of the overlay */
            onClick={(event) => event.stopPropagation()}
            onKeyDown={handleKeyDown}
            ref={containerRef}
            tabIndex={-1}
        >
            <div
                className={`travelFormInputWrapperDesktop relative flex flex-col gap-3 bg-white rounded-3 border cursor-pointer ${inputSize === 'large' ? 'justify-between w-full' : 'justify-center'} ${hasError ? 'travelFormInputWrapperDesktop--hasError' : ''} ${isOverlayVisible ? 'travelFormInputWrapperDesktop--isActive' : ''}`}
                ref={inputWrapperRef}
                style={{
                    height: inputSize === 'large' ? 60 : 45,
                    padding: inputSize === 'large' ? 10 : '3px 8px',
                }}
            >
                <div className={'p-0 text-12'}>{hasError ? errorLabel : label}</div>
                <div className={'flex justify-between items-center'}>
                    <div className={'w-full'}>
                        <input
                            autoFocus={autoFocus}
                            className={`travelFormInputDesktop ellipsis w-full h-full p-0 ${isOverlayVisible ? 'travelFormInputDesktop--isActive' : ''}`}
                            disabled={type === 'cta' || type === 'dropdown' || isInputDisabled}
                            onChange={handleOnChange}
                            onClick={handleOnClick}
                            placeholder={placeholder}
                            ref={inputRef}
                            style={{ fontSize: inputSize === 'large' ? 16 : 14 }}
                            tabIndex={0}
                            value={(isOverlayVisible ? inputValue : filterValue) ?? ''}
                        />
                        {(isInputDisabled || type === 'cta' || type === 'dropdown') && (
                            <div
                                className={'absolute w-full h-full top-0 left-0'}
                                onClick={handleOnClick}
                            />
                        )}
                    </div>
                    {isClearable && (
                        <div
                            className={'rounded-[50%] flex justify-center items-center ml-10 bg-dusty-gray'}
                            onClick={handleOnClear}
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ minHeight: 13, minWidth: 13 }}
                        >
                            <WindowCloseIcon
                                color={color('white')}
                                height={9}
                                width={9}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isOverlayVisible && (
                <div
                    className={'absolute'}
                    style={{
                        minWidth: inputOverlayMinWidth,
                        right: isAsideRightLayout ? 0 : isLastOverlay && inputSize === 'large' ? -199 : undefined,
                        top: inputSize === 'large' ? 68 : 47,
                        zIndex: zIndex('travelFormInputOverlayDesktop'),
                    }}
                >
                    <div
                        className={'relative w-full bg-white overflow-auto'}
                        // eslint-disable-next-line no-inline-styles/no-inline-styles
                        style={{ borderRadius: 10, boxShadow: `1px 3px 10px ${color('black-02')}`, maxHeight }}
                        tabIndex={-1}
                    >
                        <div
                            className={'overflow-auto'}
                            style={{ maxHeight: `${!hideCta ? `${childrenContainerMaxHeight}px` : `${maxHeight}px`}` }}
                            tabIndex={-1}
                        >
                            {children}
                        </div>
                        {!hideCta && ctaText && !isLoading && (
                            <div
                                className={'sticky w-full bg-white bottom-0'}
                                // eslint-disable-next-line no-inline-styles/no-inline-styles
                                style={{ padding: '10px 35px' }}
                            >
                                <KeyboardNavigationContainerDesktop
                                    activeOnFocus={true}
                                    containerId={'travel-form-input-cta'}
                                    hasCustomEvents={true}
                                    onBlur={() => {
                                        setIsCtaHighlighted(false);
                                        if (isTabbingForward.current) {
                                            handleOnCtaClick();
                                        }
                                    }}
                                    onFocus={() => setIsCtaHighlighted(true)}
                                >
                                    <KeyboardNavigationItemDesktop
                                        customEventKey={' '}
                                        name={'travel-form-input-desktop-cta'}
                                        onSelect={() => {
                                            handleOnCtaClick();
                                            openNextInputOverlay?.();
                                        }}
                                    >
                                        <Button
                                            disableTabIndex={true}
                                            disabled={isCtaDisabled}
                                            fontSize={18}
                                            noShadow={false}
                                            onClick={handleOnCtaClick}
                                            qaId={`qa-travel-form-input-submit-desktop-${isLoading ? 'loading' : 'loaded'}`}
                                            style={{
                                                backgroundColor: isCtaHighlighted ? color('congressBlue') : undefined,
                                            }}
                                            title={ctaText}
                                        />
                                    </KeyboardNavigationItemDesktop>
                                </KeyboardNavigationContainerDesktop>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <style jsx>{`
                .travelFormInputWrapperDesktop {
                    border-color: var(--color-white);
                    transition: all 0.1s ease-in-out;
                }
                .travelFormInputWrapperDesktop--isActive,
                .travelFormInputWrapperDesktop:hover {
                    border-color: var(--color-endeavour);
                }
                .travelFormInputWrapperDesktop--hasError {
                    border-color: var(--color-persianRed);
                }
                .travelFormInputWrapperDesktop--hasError,
                .travelFormInputWrapperDesktop--hasError input {
                    color: var(--color-persianRed);
                }
                .travelFormInputDesktop {
                    -webkit-opacity: 1; /* Override iOS opacity change affecting text & background color */
                    opacity: 1;
                    -webkit-text-fill-color: var(--color-defaultDesktop); /* Override iOS font color change */
                    color: var(--color-defaultDesktop);
                }
                .travelFormInputDesktop::placeholder {
                    -webkit-text-fill-color: var(--color-defaultDesktop);
                    color: var(--color-defaultDesktop);
                }
                .travelFormInputDesktop.travelFormInputDesktop--isActive::placeholder {
                    -webkit-text-fill-color: var(--color-doveGray);
                    color: var(--color-doveGray);
                }
            `}</style>
        </div>
    );
}
